import { useState, useEffect, useRef } from "react";
import "./Management.css";
import "./Managementeditor.css";
import { FaLinkedin, FaTimes } from "react-icons/fa";
import { CiEdit } from 'react-icons/ci';
import axios from 'axios';

const BASE_URL = "https://api.jeyshawnng.com";
const API_BASE   = `${BASE_URL}/api/management`;
const UPLOAD_URL = `${BASE_URL}/api/upload`;

const resolveSrc = (value, fallback = null) => {
  if (!value) return fallback;
  if (value instanceof File) return URL.createObjectURL(value);
  if (typeof value === "string") return value.startsWith("http") ? value : `${BASE_URL}/${value}`;
  return fallback;
};

/* ─────────────────────────────────────────────
   LOCAL CACHE HELPERS (cache-first, DB-driven)
───────────────────────────────────────────── */
const CACHE_KEY = "jeyshawn_management_data";

const getCachedData = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const setCachedData = (data) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    // storage full/unavailable — fail silently, page still works
  }
};

const newEmptyMember = (order = 0) => ({
  _id:      `new_${Date.now()}`,
  name:     "",
  role:     "",
  linkedin: "",
  featured: false,
  bio:      "",
  img:      "",
  order,
});

/* ===============================================
   IMAGE UPLOAD FIELD
=============================================== */
function ImageUploadField({ label, currentUrl, onUploaded, round = true }) {
  const [preview,   setPreview]   = useState(resolveSrc(currentUrl) || null);
  const [uploading, setUploading] = useState(false);
  const [error,     setError]     = useState(null);
  const inputRef = useRef();

  useEffect(() => { setPreview(resolveSrc(currentUrl) || null); }, [currentUrl]);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setError(null);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const { data } = await axios.post(UPLOAD_URL, formData);
      if (!data.success) throw new Error(data.message || "Upload failed");
      onUploaded(data.url);
      setPreview(resolveSrc(data.url));
    } catch (err) {
      setError("Upload failed. Try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    onUploaded("");
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="editor-img-field">
      <label>{label}</label>
      {preview ? (
        <div className="editor-img-preview-wrap">
          <img src={preview} alt="preview" className="editor-img-preview"
            style={round ? { height: 120, width: 120, objectFit: "cover", borderRadius: "50%" } : { width: "100%", maxHeight: 160, objectFit: "cover", borderRadius: 8 }} />
          {uploading && (
            <div className="editor-img-uploading">
              <div className="editor-loading__spinner editor-loading__spinner--sm" />
              <span>Uploading...</span>
            </div>
          )}
          {!uploading && (
            <div className="editor-img-actions">
              <button type="button" className="editor-img-btn editor-img-btn--change" onClick={() => inputRef.current?.click()}>Change</button>
              <button type="button" className="editor-img-btn editor-img-btn--remove" onClick={handleRemove}>Remove</button>
            </div>
          )}
        </div>
      ) : (
        <button type="button" className="editor-img-dropzone" onClick={() => inputRef.current?.click()} disabled={uploading}>
          {uploading
            ? <><div className="editor-loading__spinner editor-loading__spinner--sm" /><span>Uploading...</span></>
            : <><span className="editor-img-dropzone__icon">🖼️</span><span>Click to upload photo</span><span className="editor-img-dropzone__hint">JPG, PNG, WEBP — max 10 MB</span></>
          }
        </button>
      )}
      {error && <p className="editor-img-error">{error}</p>}
      <input ref={inputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFile} />
    </div>
  );
}

/* ===============================================
   EDIT MODAL
=============================================== */
function EditModal({ isOpen, onClose, dbData, onSaved }) {
  const [bannerImage,     setBannerImage]     = useState("");
  const [backgroundImage, setBackgroundImage] = useState("");
  const [members, setMembers] = useState([]);
  const [saving,  setSaving]  = useState(false);
  const [toast,   setToast]   = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setBannerImage(dbData?.bannerImage || "");
    setBackgroundImage(dbData?.backgroundImage || "");
    if (dbData?.members?.length > 0) {
      setMembers([...dbData.members].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
    } else {
      setMembers([newEmptyMember(0)]);
    }
  }, [isOpen, dbData]);

  if (!isOpen) return null;

  const setField = (idx, field, value) =>
    setMembers(prev => prev.map((m, i) => i === idx ? { ...m, [field]: value } : m));

  const addMember    = () => setMembers(prev => [...prev, newEmptyMember(prev.length)]);
  const removeMember = (idx) => setMembers(prev =>
    prev.filter((_, i) => i !== idx).map((m, i) => ({ ...m, order: i }))
  );
  const moveUp = (idx) => {
    if (idx === 0) return;
    setMembers(prev => {
      const a = [...prev];
      [a[idx - 1], a[idx]] = [a[idx], a[idx - 1]];
      return a.map((m, i) => ({ ...m, order: i }));
    });
  };
  const moveDown = (idx) => {
    setMembers(prev => {
      if (idx >= prev.length - 1) return prev;
      const a = [...prev];
      [a[idx], a[idx + 1]] = [a[idx + 1], a[idx]];
      return a.map((m, i) => ({ ...m, order: i }));
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        bannerImage,
        backgroundImage,
        members: members.map((m, i) => ({ ...m, order: i })),
      };
      const { data } = await axios.put(API_BASE, payload);
      if (!data.success) throw new Error(data.message || "Save failed");
      // Pass the fresh data directly back — no second fetch needed
      onSaved(data.data);
      onClose();
    } catch (err) {
      showToast("Save failed. Please try again.", "error");
    } finally {
      setSaving(false);
    }
  };

  const showToast = (msg, type) => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  return (
    <div className="em-overlay" onClick={onClose}>
      <div className="em-modal" onClick={e => e.stopPropagation()}>
        <div className="em-header">
          <h2 className="em-title">Edit Management Team</h2>
          <button className="em-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <div className="em-body">
          <p className="editor-section__title">Page Images</p>
          <ImageUploadField label="Hero / Banner Image" currentUrl={bannerImage} onUploaded={setBannerImage} round={false} />
          <ImageUploadField label="Section Background Image" currentUrl={backgroundImage} onUploaded={setBackgroundImage} round={false} />

          <p className="editor-section__title" style={{ marginTop: 20 }}>Team Members</p>
          {members.map((member, idx) => (
            <div className="em-expertise-card" key={member._id}>
              <div className="em-expertise-card-header">
                <span className="em-expertise-num">Member {String(idx + 1).padStart(2, "0")}</span>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <button className="em-remove-btn" onClick={() => moveUp(idx)}   disabled={idx === 0}                  title="Move up">↑</button>
                  <button className="em-remove-btn" onClick={() => moveDown(idx)} disabled={idx === members.length - 1} title="Move down">↓</button>
                  <button className="em-remove-btn" onClick={() => removeMember(idx)} disabled={members.length === 1}   title="Remove">&times;</button>
                </div>
              </div>
              <ImageUploadField label="Photo" currentUrl={member.img} onUploaded={url => setField(idx, "img", url)} />
              <div className="em-field">
                <label className="em-label">Name</label>
                <input className="em-input" type="text" placeholder="e.g. John Doe"
                  value={member.name} onChange={e => setField(idx, "name", e.target.value)} />
              </div>
              <div className="em-field">
                <label className="em-label">Role</label>
                <input className="em-input" type="text" placeholder="e.g. Managing Director"
                  value={member.role} onChange={e => setField(idx, "role", e.target.value)} />
              </div>
              <div className="em-field">
                <label className="em-label">LinkedIn URL</label>
                <input className="em-input" type="text" placeholder="https://linkedin.com/in/..."
                  value={member.linkedin} onChange={e => setField(idx, "linkedin", e.target.value)} />
              </div>
              <div className="em-field">
                <label className="em-label" style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={member.featured || false}
                    onChange={e => setField(idx, "featured", e.target.checked)} />
                  Featured (displayed prominently)
                </label>
              </div>
              <div className="em-field">
                <label className="em-label">Bio</label>
                <textarea className="em-textarea" rows={6}
                  placeholder="Write the member's biography. Separate paragraphs with a blank line."
                  value={member.bio || ""}
                  onChange={e => setField(idx, "bio", e.target.value)} />
              </div>
            </div>
          ))}
          <button className="editor-add-btn" style={{ marginTop: 8 }} onClick={addMember}>+ Add Member</button>
        </div>

        {toast && (
          <div className={`editor-toast${toast.type === "error" ? " editor-toast--error" : ""}`}>{toast.msg}</div>
        )}

        <div className="em-footer">
          <button className="em-btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="em-btn-save em-btn-save--footer" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ===============================================
   BIO VIEWER MODAL
=============================================== */
function MemberModal({ member, onClose }) {
  if (!member) return null;
  const bio = member.bio || "";

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <button className="modal__close" onClick={onClose} aria-label="Close"><FaTimes /></button>
        <div className="modal__left">
          {member.img && <img src={member.img} alt={member.name} className="modal__img" />}
          {member.linkedin && (
            <a href={member.linkedin} className="modal__linkedin" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
              <FaLinkedin />
            </a>
          )}
        </div>
        <div className="modal__right">
          <h2 className="modal__name">{member.name}</h2>
          <p className="modal__role">{member.role}</p>
          <div className="modal__bio">
            {bio.split("\n\n").map((para, i) => <p key={i}>{para.trim()}</p>)}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ===============================================
   MEMBER CARD
=============================================== */
function MemberCard({ member, onClick }) {
  return (
    <div
      className={`member-card${member.featured ? " member-card--featured" : ""}`}
      onClick={() => onClick(member)}
      style={{ cursor: "pointer" }}
    >
      <div className="member-card__img-wrap">
        {member.img && <img src={member.img} alt={member.name} className="member-card__img" />}
      </div>
      <div className="member-card__body">
        <h3 className="member-card__name">{member.name}</h3>
        <p className="member-card__role">{member.role}</p>
      </div>
    </div>
  );
}

/* ===============================================
   MAIN EXPORT
   Data comes only from the DB. On first ever visit
   (no cache) it loads from the API in the foreground.
   On return visits it renders instantly from
   localStorage, then silently re-checks the DB in
   the background (and on a 30s interval) and only
   updates the UI + cache if the DB data changed.
=============================================== */
export default function ManagementTeam() {
  const [dbData,         setDbData]         = useState(() => getCachedData());
  const [loading,        setLoading]        = useState(() => !getCachedData());
  const [isModalOpen,    setIsModalOpen]    = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [fetchError,     setFetchError]     = useState(null);
  const [showErrToast,   setShowErrToast]   = useState(false);

  const fetchData = async (isBackground = false) => {
    if (!isBackground) {
      setLoading(true);
      setFetchError(null);
    }
    try {
      const { data } = await axios.get(API_BASE);
      const payload = data?.data ?? data;

      if (payload) {
        const cached = getCachedData();
        const changed = JSON.stringify(cached) !== JSON.stringify(payload);
        if (changed) {
          setDbData(payload);
          setCachedData(payload);
        }
      } else if (!isBackground && !getCachedData()) {
        setDbData(null);
      }
    } catch (err) {
      // Background syncs, or foreground loads when we already have cached
      // data, fail silently so the page keeps showing what it has.
      if (!isBackground && !getCachedData()) {
        setDbData(null);
        setFetchError(err.message);
        setShowErrToast(true);
        setTimeout(() => setShowErrToast(false), 5000);
      }
    } finally {
      if (!isBackground) setLoading(false);
    }
  };

  useEffect(() => {
    const hasCache = !!getCachedData();
    fetchData(hasCache); // silent sync if we already have cached data to show

    const interval = setInterval(() => fetchData(true), 30000); // auto-check DB every 30s
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Called by EditModal with the PUT response data — instant update, no re-fetch
  const handleSaved = (freshData) => {
    setDbData(freshData);
    setCachedData(freshData);
  };

  // DB data only — members with no resolvable photo just render without one.
  const displayMembers = Array.isArray(dbData?.members)
    ? [...dbData.members]
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map(m => ({ ...m, img: resolveSrc(m.img) }))
    : [];

  const featured = displayMembers.filter(m => m.featured);
  const rest     = displayMembers.filter(m => !m.featured);

  const bannerSrc     = resolveSrc(dbData?.bannerImage);
  const backgroundSrc = resolveSrc(dbData?.backgroundImage);

  if (loading) {
    return (
      <div style={{ position: "fixed", inset: 0, background: "#0a0a0a", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
        <div style={{ width: 48, height: 48, border: "3px solid rgba(255,255,255,0.1)", borderTop: "3px solid #fff", borderRadius: "50%", animation: "spin 0.8s linear infinite", marginBottom: 20 }} />
        <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 13, letterSpacing: "0.1em", textTransform: "uppercase" }}>Loading</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <>
      <div className="img_carriersmg" style={{ position: "relative" }}>
        <button
          title="Edit page"
          onClick={() => setIsModalOpen(true)}
          style={{ position: "absolute", top: 20, right: 20, zIndex: 10, background: "rgba(255,255,255,0.9)", border: "none", borderRadius: "50%", width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}
        >
          <CiEdit size={24} color="#333" />
        </button>
        {bannerSrc && <img src={bannerSrc} alt="Team hero" />}
      </div>

      {displayMembers.length > 0 && (
        <section className="management-team" style={backgroundSrc ? { backgroundImage: `url(${backgroundSrc})` } : undefined}>
          <div className="management-team__header">
            <h2 className="management-team__title">Management Team</h2>
            <span className="management-team__divider" />
          </div>
          <div className="management-team__featured-row">
            {featured.map(m => <MemberCard key={m._id} member={m} onClick={setSelectedMember} />)}
          </div>
          <div className="management-team__grid">
            {rest.map(m => <MemberCard key={m._id} member={m} onClick={setSelectedMember} />)}
          </div>
          <MemberModal member={selectedMember} onClose={() => setSelectedMember(null)} />
        </section>
      )}

      <EditModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        dbData={dbData}
        onSaved={handleSaved}
      />

      {showErrToast && (
        <div style={{ position: "fixed", bottom: 20, right: 20, background: "#dc2626", color: "#fff", padding: "16px 20px", borderRadius: 8, display: "flex", alignItems: "center", gap: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.15)", zIndex: 9999 }}>
          <div>
            <p style={{ fontWeight: 600, margin: 0 }}>Failed to load page data</p>
            <p style={{ fontSize: 13, margin: "4px 0 0", opacity: 0.9 }}>{fetchError}</p>
          </div>
          <button onClick={() => setShowErrToast(false)} style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", fontSize: 16 }}>✕</button>
        </div>
      )}
    </>
  );
}
